import { useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { type ListEpmMasterRoleTypesQuery, useListEpmMasterRoleTypesQuery } from '@/common/graphql';
import { getRoleTypeTotal, MAX_ROLE_TYPE_ENTRIES, type RoleTypeSuggestion } from '@/common/types';
import { AllocationField } from './AllocationField';
import { AllocationPercentageInput } from './AllocationPercentageInput';
import { AllocationSelectInput } from './AllocationSelectInput';
import type { AllocationFieldWithSuggestionProps, SuggestionAppend } from './props';
import { SuggestionHint } from './SuggestionHint';
import { useGetComboboxData } from './useGetComboboxData';

interface RoleTypeSuggestionAppend extends SuggestionAppend {
  epmMasterRoleTypeId: string;
}

export const AllocationFieldRoleType = ({
  control,
  errors,
  fields,
  append,
  remove,
  watched,
  suggestions,
  showSuggestionHint,
  register,
}: AllocationFieldWithSuggestionProps<'roleType', RoleTypeSuggestion, RoleTypeSuggestionAppend>) => {
  const total = useMemo(() => getRoleTypeTotal(watched), [watched]);
  const { options, loading } = useGetComboboxData<ListEpmMasterRoleTypesQuery>(
    useListEpmMasterRoleTypesQuery,
    'listEpmMasterRoleTypes',
  );

  return (
    <AllocationField
      addLabel="Add Role Type"
      errors={errors}
      fields={fields}
      formKey="roleType"
      loading={loading}
      maxEntries={MAX_ROLE_TYPE_ENTRIES}
      title="Role Type"
      total={total}
      onAdd={() => append({ epmMasterRoleTypeId: '', percentage: 0 })}
      onRemove={remove}
      suggestionHint={
        <SuggestionHint
          show={showSuggestionHint}
          suggestions={suggestions}
          watched={watched}
          isItemDifferent={(w, s) => w.epmMasterRoleTypeId !== s.epmMasterRoleTypeId}
          renderName={s => s.name}
        />
      }
      renderRowContent={i => {
        const alreadySelected = watched.slice(0, i).map(it => it.epmMasterRoleTypeId);
        return (
          <>
            <Controller
              control={control}
              name={`roleType.${i}.epmMasterRoleTypeId`}
              render={({ field, fieldState }) => (
                <AllocationSelectInput
                  autoSelectSingle={true}
                  data={options.filter(it => !alreadySelected.includes(it.value))}
                  disabled={loading}
                  error={fieldState.error?.message}
                  placeholder="Role Type"
                  value={field.value || null}
                  onChange={val => field.onChange(val ?? '')}
                />
              )}
            />
            <AllocationPercentageInput
              registration={register(`roleType.${i}.percentage`, { valueAsNumber: true })}
              disabled={loading}
              error={errors?.roleType?.[i]?.percentage?.message}
            />
          </>
        );
      }}
    />
  );
};
